import React, {useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components';
import {sortBy} from 'lodash';
import MissionControlHeader from '../mission_control_header';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import Button from '../../../forecast-app/shared/components/buttons/button/button';
import UserSelectSideMenu from '../../../components/user_select_side_menu';
import {cloneDeep} from 'lodash';
import {showModal, MODAL_TYPE} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';

const TrialsListStyle = styled.div`
	max-height: 100vh;
	overflow: hidden;
`;

const EditUsersButton = styled.div`
	button {
		height: 30px;
	}
`;

const SignUpButtonStyle = styled.button`
	white-space: nowrap;
`;

const TableHeader = styled.thead`
	th {
		background-color: white;
		position: sticky;
		top: 0;
	}
`;

const TableBody = styled.tbody``;

const SortableTableHeaderCellStyle = styled.th`
	text-decoration: underline;
	width: 1%;
	white-space: nowrap;
`;

const SortableTableHeaderCell = ({label, property, sortValue, onClick}) => {
	const sortDirection = sortValue.property === property ? (sortValue.desc ? '▼' : '▲') : null;

	return (
		<SortableTableHeaderCellStyle onClick={() => onClick(property)}>
			{label}
			{sortDirection}
		</SortableTableHeaderCellStyle>
	);
};

const TrialsList = props => {
	const {
		viewer: {
			missionControlData: {trialsList},
		},
	} = props;

	const [searchValue, setSearchValue] = useState('');
	const [sortValue, setSortValue] = useState({property: 'companyId', desc: false});
	const [companyUsers, setCompanyUsers] = useState([]);
	const [showSideMenu, setShowSideMenu] = useState(false);

	const sortByProperty = property => setSortValue({property, desc: property !== sortValue.property ? false : !sortValue.desc});

	const filteredTrialsList = sortBy(
		trialsList.filter(trial => {
			return trial.companyName.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
		}),
		[sortValue.property]
	);

	if (sortValue.desc) {
		filteredTrialsList.reverse();
	}

	const toggleEditUserMenu = persons => {
		setCompanyUsers(persons);
		setShowSideMenu(!showSideMenu);
	};

	const handleUserActivatedChange = userId => {
		const users = cloneDeep(companyUsers);
		const user = users.find(user => user.id === userId);
		user.active = !user.active;
		setCompanyUsers(users);
	};

	const openSignupModal = companyId => {
		showModal({
			type: MODAL_TYPE.SIGNUP_STANDALONE,
			companyId,
			addons: props.viewer.chargebeeAddon,
			plans: props.viewer.chargebeePlan,
		});
	};

	return (
		<TrialsListStyle>
			<UserSelectSideMenu
				onBlur={() => toggleEditUserMenu([])}
				shown={showSideMenu}
				users={companyUsers}
				viewerPersonId={props.viewer.actualPersonId}
				viewerId={props.viewer.id}
				isFromMissionControl={true}
				handleUserActivatedChange={handleUserActivatedChange}
			/>
			<div className="mission-control-list">
				<MissionControlHeader viewer={props.viewer} />
				<h1>Trials</h1>
				<div>
					<input type="search" placeholder="Search" value={searchValue} onChange={event => setSearchValue(event.target.value)} />
				</div>
				<div style={{maxHeight: '90vh', overflowY: 'scroll'}}>
					<table>
						<TableHeader>
							<tr>
								<SortableTableHeaderCell sortValue={sortValue} onClick={sortByProperty} label="ID" property="companyId" />
								<SortableTableHeaderCell sortValue={sortValue} onClick={sortByProperty} label="Company" property="companyName" />
								<SortableTableHeaderCell sortValue={sortValue} onClick={sortByProperty} label="Trial End" property="trialEnd" />
								<SortableTableHeaderCell sortValue={sortValue} onClick={sortByProperty} label="Contact Name" property="contactName" />
								<SortableTableHeaderCell sortValue={sortValue} onClick={sortByProperty} label="Email" property="contactEmail" />
								<th>&nbsp;</th>
							</tr>
						</TableHeader>
						<TableBody>
							{filteredTrialsList.map(trial => {
								return (
									<tr key={trial.id}>
										<td style={{verticalAlign: 'middle', width: '1%'}}>{trial.companyId}</td>
										<td style={{verticalAlign: 'middle', width: '1%', whiteSpace: 'nowrap'}}>{trial.companyName}</td>
										<td style={{verticalAlign: 'middle', width: '1%', whiteSpace: 'nowrap'}}>{`${trial.trialEndYear}-${trial.trialEndMonth}-${trial.trialEndDay} ${trial.trialExpired ? ' (Expired)' : ''}`}</td>
										<td style={{verticalAlign: 'middle', width: '1%', whiteSpace: 'nowrap'}}>{trial.contactName}</td>
										<td style={{verticalAlign: 'middle', width: '1%', whiteSpace: 'nowrap'}}>{trial.contactEmail}</td>
										<td style={{verticalAlign: 'middle'}}>
											<EditUsersButton>
												<Button
													id="edit-users-btn"
													text={'Edit Current Users'}
													colorTheme={BUTTON_COLOR.VERYLIGHTGREY}
													buttonStyle={BUTTON_STYLE.OUTLINE}
													onClick={() => toggleEditUserMenu(trial.persons)}
													className={'edit-virtual-users-link'}
												/>
											</EditUsersButton>
										</td>
										<td style={{verticalAlign: 'middle'}}>
											<SignUpButtonStyle onClick={() => openSignupModal(trial.companyId)}>Sign Up</SignUpButtonStyle>
										</td>
									</tr>
								);
							})}
						</TableBody>
					</table>
				</div>
			</div>
		</TrialsListStyle>
	);
};

const trialsListQuery = graphql`
	query trialsList_Query {
		viewer {
			actualPersonId
			component(name: "control_trials_list")
			...trialsList_viewer
		}
	}
`;

export {trialsListQuery};

export default createFragmentContainer(TrialsList, {
	viewer: graphql`
		fragment trialsList_viewer on Viewer {
			id
			...missionControlHeader_viewer
			chargebeePlan {
				chargebeeId
				currency
				price
				period
				periodUnit
			}
			chargebeeAddon {
				chargebeeId
				currency
				price
			}
			missionControlData {
				trialsList {
					companyId
					companyName
					jiraEnabled
					minimumSeats
					minimumVirtualSeats
					users
					virtualUsers
					trialEndDay
					trialEndMonth
					trialEndYear
					contactName
					contactEmail
					trialExpired
					persons {
						id
						active
						role {
							id
							name
						}
						firstName
						lastName
						fullName
						profilePictureId
						email
						permissions
					}
				}
			}
		}
	`,
});
